import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useRef, useState } from 'react';

import { CoachSummary } from '@/components/CoachSummary';
import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, LoadingScreen, Screen, type ScreenHandle, ScreenHeading } from '@/modules/ui';

import { MessageRow } from './components/MessageRow';

// Stable ids for the preset messages. Selection is tracked by id, not by the
// translated label, so a mid-session Transifex update (which changes the label
// strings) can't orphan the selection — and the actual message text is resolved
// from the current locale at render + send time.
const INTRO_MESSAGE_IDS = ['excited', 'ready', 'meet'] as const;
type IntroMessageId = (typeof INTRO_MESSAGE_IDS)[number];

/**
 * QUICK INTRO MESSAGES — pick a preset intro message for the new coach (Sparta's
 * QuickIntroMessagesContainer). Continue sends the chosen message into the
 * coach's care-team Stream channel (via `sendIntroMessage`). On success it
 * advances; on failure it does NOT silently advance — it surfaces an error at
 * the top and flips the CTA to "Try again" so the user can re-attempt. "Skip for
 * now" always advances without sending (the escape hatch).
 */
export function QuickIntroMessagesScreen() {
  const t = useT();
  const {
    user,
    matchedCoaches,
    selectedCoachUuid,
    sendIntroMessage,
    markProviderMatchingComplete,
  } = useRulesEngine();

  const coach =
    matchedCoaches?.coaches.find((c) => c.uuid === selectedCoachUuid) ?? matchedCoaches?.coaches[0];

  const messageById: Record<IntroMessageId, string> = {
    excited: t('“Hi, I’m excited to start my healthy journey!”'),
    ready: t('“Hi! I’m ready to feel better.”'),
    meet: t('“Hi! Nice to meet you.”'),
  };
  const [selected, setSelected] = useState<IntroMessageId | null>(null);
  const [validationError, setValidationError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // True after a send failed — surfaces the error at the top and flips the CTA
  // to "Try again". Cleared on a new attempt or a fresh selection.
  const [sendFailed, setSendFailed] = useState(false);
  const screenRef = useRef<ScreenHandle>(null);

  // End the coach phase by marking provider matching complete, triggering
  // the driver route to the next step — member-contact on the experiment,
  // else the terminal transition screen. `assignPrimaryCoach` can't complete
  // this any earlier without the driver skipping this screen (see IT-C2).
  const advance = () => markProviderMatchingComplete();

  const onSkip = () => {
    trackEvent('intro-message-click-skip', { uuid: user?.uuid, coachUuid: coach?.userUuid });
    advance();
  };

  const onContinue = async () => {
    // Continue stays enabled with nothing selected so the press surfaces a
    // validation message (scrolled into view) rather than silently advancing.
    // "Skip for now" still advances without a selection.
    //
    // INTENTIONAL Sparta divergence: Sparta treats an empty Continue as a skip
    // (one handler, no validation). We keep the explicit validate-or-Skip pattern
    // used across the other onboarding screens — a deliberate RN UX choice, not a gap.
    if (!selected) {
      setValidationError(true);
      screenRef.current?.scrollToTop();
      return;
    }
    const messageContent = messageById[selected];
    setSendFailed(false);
    setIsSubmitting(true);
    const sent = await sendIntroMessage(messageContent);
    if (sent) {
      trackEvent('intro-message-click-send', {
        uuid: user?.uuid,
        messageContent,
        coachUuid: coach?.userUuid,
        status: 'success',
      });
      // Leave isSubmitting true to prevent interaction while waiting for
      // the driver to navigate.
      advance();
      return;
    }
    setIsSubmitting(false);
    // Do NOT silently advance on failure — surface the error and let the user
    // retry (the CTA flips to "Try again"). "Skip for now" stays the escape hatch.
    trackEvent('intro-message-send-failed', { uuid: user?.uuid, coachUuid: coach?.userUuid });
    setSendFailed(true);
    screenRef.current?.scrollToTop();
  };

  if (!coach) return <LoadingScreen />;

  return (
    <Screen
      ref={screenRef}
      footer={
        <VStack space='md'>
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onContinue}
            testID='quick-intro-continue'
          >
            {sendFailed ? <T _str='Try again' /> : <T _str='Continue' />}
          </CtaButton>
          <CtaButton
            type='secondary'
            isDisabled={isSubmitting}
            onPress={onSkip}
            testID='quick-intro-skip'
          >
            <T _str='Skip for now' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='4xl'>
        <ScreenHeading title={t('Send a quick intro message to your provider')} />
        <CoachSummary coach={coach} />

        {/* Both error banners live here so validation and send errors surface in
            the same spot after a scrollToTop, rather than jumping position. */}
        {validationError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Please select a message to continue' />
          </Text>
        ) : null}
        {sendFailed ? (
          <Text size='sm' className='text-error-700' testID='quick-intro-send-error'>
            <T _str="We couldn't send your message. Please try again." />
          </Text>
        ) : null}

        <VStack space='lg'>
          {INTRO_MESSAGE_IDS.map((id) => (
            <MessageRow
              key={id}
              label={messageById[id]}
              selected={selected === id}
              testID={`quick-intro-message-${id}`}
              onPress={() => {
                setSelected(id);
                setValidationError(false);
                setSendFailed(false);
                trackEvent('intro-message-selected-option', {
                  uuid: user?.uuid,
                  messageContent: messageById[id],
                  coachUuid: coach?.userUuid,
                });
              }}
            />
          ))}
        </VStack>
      </VStack>
    </Screen>
  );
}
